import React, { useState, useMemo } from 'react';
import { useAppSelector } from 'redux/hooks';
import styled from 'styled-components';
import { useMount } from 'react-use';
import { Helmet } from 'react-helmet-async';
import StickyContainer from 'src/components/StickyContainer';
import {
  Grid,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Link,
  Tooltip,
  Box,
  Card,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import { green, red } from '@mui/material/colors';
import { spacing, SpacingProps } from '@mui/system';
import MappingStatsPerDataTypesTableV2 from 'IntegrationModule/components/MappingStatsPerDataTypes/MappingStatsPerDataTypesTableV2';
import DataHistory from 'IntegrationModule/components/DataHistory';
import { ErrorLogTable } from 'shared';
import Table from 'components/Table';
import { setTargetService } from 'actionCreators';
import colors from 'theme/colors';
import BarChart from '../../../pages/reports/components/BarChart';
import Stats from '../../../pages/reports/components/Stats';
import TrafficTable from '../../../pages/reports/components/TrafficTable';
import DataTypeUpdatesOverview from 'IntegrationModule/components/DataTypeUpdatesOverview/DataTypeUpdatesOverview';
import useIntegrationOptions, {
  IntegrationOptions,
} from 'IntegrationModule/components/IntegrationOptions/useIntegrationOptions';

import {
  selectIsDevMode,
  selectTeamHash,
  selectSelectedTargetService,
  makeGetSelectIntegrationByTeamIdAndTargetServiceId,
} from 'selectors';

import useDataHistory from 'hooks/useDataHistory';
import useUrl from 'hooks/useUrl';
import useIntegrationServerQueuesStatusTable from 'hooks/useIntegrationServerQueuesStatusTable';
import useAppNavigation from 'hooks/useAppNavigation';
import useErrorLogs from 'hooks/useErrorLogs';
import { useAppDispatch } from 'hooks';
import useSimpleTodoList from 'hooks/useSimpleTodoList';
import StandardPageHeader from 'components/StandardPageHeader';

import {
  AVAILABLE_INTEGRATION_SERVICES,
  integrationsHash,
  TEAM,
  INTEGRATION_SERVICE_NAMES,
} from '@constants';

import {
  fetchTeams,
  updateIntegrationNotes,
  fetchIntegrations,
  fetchIntegrationSettingSchema,
} from 'thunk';

import {
  ErrorInterface,
  ExceedRetriesInstance,
  Item,
  RecentErrorsType,
  FilterStateInterface,
  IntegrationNote,
} from 'types';

import {
  conditionallyRenderElement,
  checkIsTimeEntrySendToAgent,
  checkIsTimeEntrySendToMosaic,
  getTeamProfileUrl,
  getReportTeamsPageDeepLinkUrl,
  getTeamsV2PageUrl,
  checkIsodbcSupportIntegrations,
} from 'utils';

import useIntegrationHealthCalendar from 'hooks/useIntegrationHealthCalendar';
import useCsvImportStatus from 'hooks/useCsvImportStatus';
import AutoImportIcon from 'assets/AutoImportIcon';
import TwoWaySyncIcon from 'assets/TwoWaySyncIcon';
import { AppStateType } from 'reducers';
import CsvImportStatusTable from 'IntegrationModule/components/CsvImportStatus/CsvImportStatusTable';
import { FetchIntegrationsSettingSchemaSuccessResponse } from 'IntegrationModule/types';
import { useCheckIsBakIntegrationQuery } from 'IntegrationModule/api/queries/useCheckIsBakIntegrationQuery';
import { useIntegrationsListQuery } from 'IntegrationModule/api/queries/useIntegrationsListQuery';
import { IntegrationStateIndicator } from 'IntegrationModule/components/IntegrationStateIndicator';
import { getEffectiveState } from 'IntegrationModule/utils/utils';

const CsvImportStatusTableContainer = styled.div``;

const Divider = styled(MuiDivider)(spacing as any);

const Typography = styled(MuiTypography)<SpacingProps>(spacing);

const CalendarStyler = styled.div`
  .fc-daygrid-event {
    display: flex;
    justify-content: center;
    background-color: transparent !important;
  }
`;

type TeamProfileUrlParams = Parameters<typeof getTeamProfileUrl>[0];

type IntegrationBreadcrumbInputs = {
  urlTenant?: TeamProfileUrlParams['tenant'];
  urlRealmId?: TeamProfileUrlParams['realmId'];
  teamIdToUse: TeamProfileUrlParams['teamId'];
  targetServiceLabel: string;
  targetServiceIdToUse: string | number;
};

/** Home > [tenant] > [realm] > team > integration > integration id. */
const buildIntegrationBreadcrumbs = ({
  urlTenant,
  urlRealmId,
  teamIdToUse,
  targetServiceLabel,
  targetServiceIdToUse,
}: IntegrationBreadcrumbInputs): React.ComponentProps<
  typeof StandardPageHeader
>['customBreadcrumbs'] => [
  { label: 'Home', path: '/' },
  // Add Tenant if available from URL - clickable to filter teams by tenant
  ...(urlTenant ? [{ label: urlTenant, path: getTeamsV2PageUrl({ tenant: urlTenant }) }] : []),
  // Add Realm if available from URL - clickable to filter teams by tenant+realm
  ...(urlRealmId
    ? [
        {
          label: urlRealmId,
          path: getTeamsV2PageUrl({ tenant: urlTenant, realmId: urlRealmId }),
        },
      ]
    : []),
  // Team ID - clickable to go back to team profile with tenant/realm context
  {
    label: `Team ${teamIdToUse}`,
    path: getTeamProfileUrl({
      teamId: teamIdToUse,
      tenant: urlTenant,
      realmId: urlRealmId,
    }),
  },
  // Integration name - clickable (we're already here but it's not the last item)
  { label: targetServiceLabel },
  // Integration ID (not clickable - we're here)
  { label: `${targetServiceIdToUse}` },
];

type TwoWaySyncTooltipProps = {
  timeEntrySendToAgent: ReturnType<typeof checkIsTimeEntrySendToAgent>;
  timeEntrySendToMosaic: ReturnType<typeof checkIsTimeEntrySendToMosaic>;
  targetServiceLabel: string;
};

const TwoWaySyncTooltipText = ({
  timeEntrySendToAgent,
  timeEntrySendToMosaic,
  targetServiceLabel,
}: TwoWaySyncTooltipProps) => (
  <span>
    {`Time entry ${
      timeEntrySendToAgent ? 'is' : 'is not'
    } sent to ${targetServiceLabel}; time entry ${
      timeEntrySendToMosaic ? 'is' : 'is not'
    } sent to Mosaic`}
  </span>
);

/** Placeholder report widgets, only rendered in dev mode. */
const DevModeReportsSection = ({ isDevMode }: { isDevMode: boolean }) => (
  <React.Fragment>
    <Grid style={{ display: isDevMode ? 'flex' : 'none' }} container spacing={6}>
      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
        <Stats
          title="Visitors"
          amount="24.532"
          chip="Today"
          percentageText="+14%"
          percentagecolor={green[500]}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
        <Stats
          title="Activity"
          amount="63.200"
          chip="Annual"
          percentageText="-12%"
          percentagecolor={red[500]}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
        <Stats
          title="Real-Time"
          amount="1.320"
          chip="Monthly"
          percentageText="-18%"
          percentagecolor={red[500]}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
        <Stats
          title="Bounce"
          amount="12.364"
          chip="Yearly"
          percentageText="+27%"
          percentagecolor={green[500]}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 12 }}>
        <TrafficTable />
      </Grid>
    </Grid>

    <Grid style={{ display: isDevMode ? 'flex' : 'none' }} container spacing={6}>
      <Grid size={{ xs: 12, lg: 8 }}>{/* <WorldMap /> */}</Grid>
      <Grid size={{ xs: 12, lg: 4 }}>
        <BarChart />
      </Grid>
    </Grid>
  </React.Fragment>
);

function IntegrationDetails() {
  const dispatch = useAppDispatch();
  const {
    targetServiceId: pathTargetServiceId,
    targetService: pathTargetService,
    teamId: pathTeamId,
    queryParams,
  } = useUrl();
  const { navigateToTeamsTable } = useAppNavigation();
  const teamHash = useAppSelector(selectTeamHash);
  const selectedTargetService = useAppSelector(selectSelectedTargetService);
  const isDevMode = useAppSelector(selectIsDevMode);
  const { IntegrationOptionsMenu, ...integrationOptionsProps } = useIntegrationOptions();
  /* ------------------------------ Values To Use ----------------------------- */
  const selectedTeam = teamHash[pathTeamId];
  const teamIdToUse = pathTeamId || selectedTeam?.id;
  const teamNameToUse = selectedTeam?.name;

  // Get tenant/realm from URL query params for breadcrumb context
  // Use || undefined (not || "") so nullish coalescing (??) fallback works correctly in navigation functions
  const urlTenant = queryParams?.get('tenant') || undefined;
  const urlRealmId = queryParams?.get('realmId') || undefined;
  const targetServiceToUse = pathTargetService || selectedTargetService?.name;
  const targetServiceIdToUse = pathTargetServiceId || selectedTargetService?.id;
  const isCsvAgent = targetServiceToUse === INTEGRATION_SERVICE_NAMES.CSV_AGENT;
  // Same params as the team page's Integration List, so the cache is shared; the
  // row menu uses them to block Unarchive on a duplicate csv-agent (GLX-2091).
  // The path teamId is a string and the team page keys the list by number. Without
  // a team id the page redirects away, so the request is skipped rather than sent with NaN.
  const teamIdNumber = Number(teamIdToUse);
  const { integrationsList: teamIntegrations, isIntegrationsListLoaded } = useIntegrationsListQuery(
    {
      mosaicTeamId: teamIdNumber,
      includeArchived: true,
      includeDrafts: true,
      tenant: urlTenant,
      realmId: urlRealmId,
    },
    { enabled: Boolean(teamIdToUse) && Number.isFinite(teamIdNumber) },
  );
  const [hasOdbc, setHasOdbc] = useState(false);

  const selectIntegrationByTeamIdAndTargetServiceId = useMemo(
    () => makeGetSelectIntegrationByTeamIdAndTargetServiceId(),
    [],
  );
  const { data: currentIntegration } = useAppSelector((state: AppStateType) =>
    selectIntegrationByTeamIdAndTargetServiceId(state, {
      teamId: teamIdToUse,
      targetServiceId: targetServiceIdToUse,
    }),
  );
  const notes = useMemo(() => currentIntegration?.notes || [], [currentIntegration?.notes]);

  /* --------------------------- Csv Upload Tracker --------------------------- */
  const csvImportStatusHookReturnValues = useCsvImportStatus();
  const { fetchCsvImportStatus } = csvImportStatusHookReturnValues;

  useMount(() => {
    if (
      !teamIdToUse ||
      !targetServiceToUse ||
      !targetServiceIdToUse ||
      !AVAILABLE_INTEGRATION_SERVICES.has(targetServiceToUse)
    ) {
      navigateToTeamsTable();
    } else {
      void dispatch(
        setTargetService({
          name: targetServiceToUse as string,
          id: targetServiceIdToUse,
        }),
      );
      void dispatch(
        fetchTeams({
          teamIds: [teamIdToUse],
        }),
      );
      void dispatch(
        fetchIntegrations({
          targetServiceId: targetServiceIdToUse,
          includeArchived: true,
          onSuccess: ({ response }) => {
            const { data } = response || {};
            const { notes } = data?.[0] || {};

            if (notes) {
              void dispatch(
                updateIntegrationNotes({
                  notes: notes.map((note: IntegrationNote) => ({
                    ...note,
                    isEditable: false,
                  })),
                  teamId: teamIdToUse,
                  skipAlertOnSuccess: true,
                  targetServiceId: targetServiceIdToUse,
                }),
              );
            }
          },
        }),
      );
      if (checkIsodbcSupportIntegrations(targetServiceToUse)) {
        void dispatch(
          fetchIntegrationSettingSchema({
            targetServiceId: targetServiceIdToUse,
            onSuccess: ({
              response,
            }: {
              response: { data: FetchIntegrationsSettingSchemaSuccessResponse };
            }) => {
              const odbcData = response.data.agentSchema.properties.credentials.properties.odbc;
              const odbcDataSource = odbcData.properties.dataSource;
              setHasOdbc(Boolean(odbcDataSource?.default));
            },
          }),
        );
      }

      if (isCsvAgent) {
        fetchCsvImportStatus({
          teamId: teamIdToUse,
          targetServiceId: targetServiceIdToUse,
        });
      }
    }
  });

  // const { IntegrationHealthByIdTable } = useIntegrationHealthData({
  //   targetServiceId: targetServiceIdToUse,
  //   targetService: targetServiceToUse,
  //   teamId: teamIdToUse,
  // });

  const {
    dataHistoryTabsList,
    dataHistoryTabIndex,
    dataHistoryMenuAnchor,
    isDataTypesMenuOpen,
    selectedDataType,
    dataHistorydata,
    canFetchAfter,
    canFetchBefore,
    dataHistoryOnHandleTabChange,
    dataTypesMenuOnClose,
    dataTypesMenuOnClick,
    dataTypesMenuOnItemClick,
    defaultDataHistoryMenuText,
    getDataIntervalAfter,
    getDataIntervalBefore,
    isDataHistoryLoading,
    showAlert,
    toggleAlert,
    dataHistoryError,
  } = useDataHistory({ teamId: teamIdToUse });

  const {
    renderTable: renderQueuesStatusTable,
    renderTableToolbar: renderQueuesStatusTableToolbar,
    queuesStatusTableError,
    isQueuesStatusTableLoading,
  } = useIntegrationServerQueuesStatusTable();

  const {
    isLoading: errorLogLoading,
    error: errorLogError,
    data: errorLogData,
    filterState: errorLogFilter,
    collapseProps: errorLogDataCollapseProps,
    total: errorLogTotalCounts,
    headCells: errorLogHeadCells,
    filtersToShow: errorLogFiltersToShow,
    onRequestSort: errorLogOnRequestSort,
    onPageChange: errorLogOnPageChange,
    onAllCollapse: errorLogOnAllCollapse,
    fetchNew: errorLogFetchNew,
    errorType,
  } = useErrorLogs({
    targetServiceName: targetServiceToUse,
    mosaicTeamId: teamIdToUse,
    targetServiceId: targetServiceIdToUse,
    viewBy: TEAM,
  });

  const targetServiceLabel =
    integrationsHash[targetServiceToUse as keyof typeof integrationsHash].label;
  const currentIntegrationSettings = currentIntegration?.settings;

  /* ----------------------- Integration Health Calendar ---------------------- */
  const { IntegrationHealthCalendar } = useIntegrationHealthCalendar({
    targetService: targetServiceToUse,
    targetServiceId: targetServiceIdToUse,
    teamId: teamIdToUse,
  });

  // const { TableComponent: IntegrationHealthTable } = useIntegrationHealthTable({
  //   targetService: targetServiceToUse,
  //   targetServiceId: targetServiceIdToUse,
  //   teamId: teamIdToUse,
  // });

  /* ---------------------------- Integration Notes --------------------------- */

  const handleUpdateIntegrationNotes = ({ fields, values, actions }: any) => {
    const { setSubmitting } = actions;

    void dispatch(
      updateIntegrationNotes({
        notes: fields,
        targetServiceId: targetServiceIdToUse,
        teamId: teamIdToUse,
        onDone: () => {
          setSubmitting(false);
        },
      }),
    );
  };

  const { List: IntegrationNotes } = useSimpleTodoList({
    fields: notes,
    formId: 'integration-notes',
    onSubmit: handleUpdateIntegrationNotes,
  });
  const effectiveState = getEffectiveState(currentIntegration);
  const timeEntrySendToAgent = checkIsTimeEntrySendToAgent(currentIntegrationSettings);
  const timeEntrySendToMosaic = checkIsTimeEntrySendToMosaic(currentIntegrationSettings);
  const twoWaySyncTooltip = (
    <TwoWaySyncTooltipText
      timeEntrySendToAgent={timeEntrySendToAgent}
      timeEntrySendToMosaic={timeEntrySendToMosaic}
      targetServiceLabel={targetServiceLabel}
    />
  );

  const isAutoImportProject = currentIntegrationSettings?.autoLinkProject;

  const { isBakIntegrationData, isBakIntegrationError } = useCheckIsBakIntegrationQuery({
    serviceName: targetServiceToUse,
    targetServiceId: targetServiceIdToUse,
  });
  const isBakIntegration = isBakIntegrationData?.isBakIntegration || false;

  return (
    <React.Fragment>
      <Helmet title={`${teamIdToUse} | ${targetServiceIdToUse} | ${teamNameToUse}`} />

      <Box
        sx={{
          padding: 4,
        }}
      >
        <StandardPageHeader
          title={`${teamNameToUse}: ${targetServiceLabel}`}
          subtitle={
            currentIntegrationSettings?.label
              ? `${currentIntegrationSettings.label}`
              : 'Team-specific integration management'
          }
          icon="Integration"
          sx={{ mb: 4 }}
          customBreadcrumbs={buildIntegrationBreadcrumbs({
            urlTenant,
            urlRealmId,
            teamIdToUse,
            targetServiceLabel,
            targetServiceIdToUse,
          })}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 4,
          }}
        >
          <IntegrationStateIndicator
            state={effectiveState}
            deletionInProgress={currentIntegration?.deletionInProgress}
          />
          <Tooltip title={twoWaySyncTooltip}>
            <span>
              <TwoWaySyncIcon
                width="30"
                height="30"
                primaryColor={timeEntrySendToAgent ? colors.successGreen : colors.unlinkGrey}
                secondaryColor={timeEntrySendToMosaic ? colors.successGreen : colors.unlinkGrey}
              />
            </span>
          </Tooltip>
          {isAutoImportProject ? (
            <Tooltip title="Project auto import">
              <span>
                <AutoImportIcon width="25" height="25" color="#008000" />
              </span>
            </Tooltip>
          ) : null}
          <IntegrationOptions
            targetServiceId={targetServiceIdToUse}
            state={effectiveState}
            isBakIntegration={isBakIntegration}
            teamId={teamIdToUse}
            targetService={targetServiceToUse}
            customDailyFetchCount={currentIntegration?.settings?.customDailyFetchCount}
            customDailyFetchTime={currentIntegration?.settings?.customDailyFetchTime}
            tenant={urlTenant}
            realmId={urlRealmId}
            showOdbcChip={hasOdbc}
            showBakChip={isBakIntegration && !isBakIntegrationError}
            // Undefined until loaded, so a deep link can't unarchive a duplicate csv-agent.
            teamIntegrations={isIntegrationsListLoaded ? teamIntegrations : undefined}
            syncFrequencyUpdatedOnSuccess={({ targetServiceId }) => {
              // The mutation only invalidates the react-query cache; this page reads
              // the integration from redux, so refetch it to reflect the new count.
              void dispatch(fetchIntegrations({ targetServiceId, includeArchived: true }));
            }}
            {...integrationOptionsProps}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 6 }} />
      <MappingStatsPerDataTypesTableV2
        targetService={targetServiceToUse}
        targetServiceId={+targetServiceIdToUse}
        teamId={teamIdToUse}
      />

      <Grid container spacing={6}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <CalendarStyler>{IntegrationHealthCalendar}</CalendarStyler>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <DataTypeUpdatesOverview
            targetServiceId={targetServiceIdToUse}
            targetService={targetServiceToUse}
          />
        </Grid>
      </Grid>

      {isCsvAgent && (
        <Grid>
          <CsvImportStatusTableContainer>
            <CsvImportStatusTable
              {...csvImportStatusHookReturnValues}
              refetchCsvImportStatus={() =>
                fetchCsvImportStatus({
                  teamId: teamIdToUse,
                  targetServiceId: targetServiceIdToUse,
                })
              }
            />
          </CsvImportStatusTableContainer>
        </Grid>
      )}

      <Grid container spacing={6}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <DataHistory
            {...({
              tabListValue: dataHistoryTabIndex,
              data: dataHistorydata.data,
              chartOptions: dataHistorydata.chartOptions,
              tabsList: dataHistoryTabsList,
              selectedDataType: selectedDataType,
              handleTabsChange: dataHistoryOnHandleTabChange,
              dataTypesMenuOnItemClick: dataTypesMenuOnItemClick,
              getDataIntervalBefore: getDataIntervalBefore,
              getDataIntervalAfter: getDataIntervalAfter,
              canFetchBefore: canFetchBefore,
              canFetchAfter: canFetchAfter,
              isLoading: isDataHistoryLoading,
              showAlert: showAlert,
              error: dataHistoryError,
              toggleAlert: toggleAlert,
            } as any)}
          />
        </Grid>

        {/* Queue status table  */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card
            style={{
              padding: '0px 20px 20px 20px',
              height: '37rem',
              overflow: 'auto',
              marginTop: '20px',
            }}
          >
            <Table
              hidePagination
              renderTableToolbar={renderQueuesStatusTableToolbar}
              renderTable={renderQueuesStatusTable}
              tableTitle="Queues status"
              isLoading={isQueuesStatusTableLoading}
              error={queuesStatusTableError}
            />
          </Card>
        </Grid>
      </Grid>

      {/* <Grid container spacing={6}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Settings targetServiceId={targetServiceIdToUse} />
        </Grid>
      </Grid> */}

      <ErrorLogTable
        targetService={targetServiceToUse}
        filterState={errorLogFilter as FilterStateInterface}
        collapseProps={errorLogDataCollapseProps}
        filtersToShow={errorLogFiltersToShow}
        total={errorLogTotalCounts}
        error={errorLogError as ErrorInterface}
        isLoading={errorLogLoading}
        headCells={errorLogHeadCells}
        data={errorLogData as unknown as Array<RecentErrorsType>}
        tableTitle="Error Log"
        onPageChange={errorLogOnPageChange}
        onRequestSort={errorLogOnRequestSort}
        onAllCollapse={errorLogOnAllCollapse}
        errorType={errorType}
        onFetchNew={errorLogFetchNew}
      />
      <DevModeReportsSection isDevMode={isDevMode} />
      {IntegrationOptionsMenu}
    </React.Fragment>
  );
}

export default IntegrationDetails;
